// src/context/ThemeContext.js
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { createTheme } from "@mui/material/styles";
import { LanguageContext } from "./LanguageContext";

const ThemeContext = createContext(null);

const STORAGE_KEY = "themeMode"; // "dark" | "light"
const normalizeThemeMode = (mode) => (mode === "light" ? "light" : "dark");

const applyThemeMode = (mode) => {
  const normalizedMode = normalizeThemeMode(mode);

  if (typeof document !== "undefined") {
    const root = document.documentElement;
    root.classList.remove("light-theme", "dark-theme");
    root.classList.add(normalizedMode === "dark" ? "dark-theme" : "light-theme");
  }

  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, normalizedMode);
  }

  return normalizedMode;
};

export function ThemeProviderApp({ children }) {
  const [modeState, setModeState] = useState(() => {
    if (typeof window === "undefined") {
      return "dark";
    }

    const initialMode = window.localStorage.getItem(STORAGE_KEY) || "dark";
    return applyThemeMode(initialMode);
  });
  const modeRef = useRef(modeState);
  const { lang } = useContext(LanguageContext);
  const direction = ["ar", "ur"].includes(lang) ? "rtl" : "ltr";

  const setMode = useCallback((nextMode) => {
    const resolvedMode =
      typeof nextMode === "function"
        ? nextMode(modeRef.current)
        : nextMode;
    const normalizedMode = applyThemeMode(resolvedMode);

    modeRef.current = normalizedMode;
    setModeState(normalizedMode);
  }, []);

  const mode = modeState;

  const muiTheme = useMemo(
    () =>
      createTheme({
        direction,
        palette: { mode },
      }),
    [direction, mode]
  );

  const value = useMemo(
    () => ({
      mode,
      setMode,
      toggle: () => setMode((m) => (m === "dark" ? "light" : "dark")),
      muiTheme,
    }),
    [mode, muiTheme, setMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useAppTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useAppTheme must be used inside ThemeProviderApp");
  return ctx;
}
